import React, { useState, useEffect, useMemo } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  TextField,
  Typography,
  Link,
  makeStyles,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { DriveCompany } from 'lib/Model/Drive';
import { HierarchyCompany } from 'lib/Model/TermsGenerator';
import { createDriveFolder } from 'lib/Api/Drive';
import { createTerms, createConsolidatedTerms } from 'lib/Api/TermsGenerator';
import { CreateConsolidatedTermsResponse } from 'lib/Model/TermsGenerator';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import DescriptionIcon from '@material-ui/icons/Description';
import LaunchIcon from '@material-ui/icons/Launch';

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    minWidth: '600px',
  },
  formControl: {
    margin: theme.spacing(1, 0, 2),
    width: '100%',
  },
  infoBox: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  folderSearchBox: {
    marginTop: theme.spacing(2),
  },
  successContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: theme.spacing(2),
  },
  successLink: {
    marginTop: theme.spacing(2),
  },
  folderOptions: {
    marginTop: theme.spacing(2),
    marginLeft: theme.spacing(4),
  },
  radioGroup: {
    flexDirection: 'column',
  },
  warningList: {
    paddingLeft: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
  successActions: {
    marginTop: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'center',
  },
  consolidatedInfo: {
    marginBottom: theme.spacing(2),
  },
  companyList: {
    paddingLeft: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
  missingFolderWarning: {
    marginTop: theme.spacing(2),
  },
}));

interface TermsGenerationDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  company: DriveCompany | null;
  companyName: string;
  registrationNumber: string;
  registrationType: 'registration' | 'vat' | 'other';
  address: string;
  signatory: string;
  dateOfIssue: Date;
  brand: 'SIM' | 'Konnekt' | 'Talexio';
  country?: 'Malta' | 'Cyprus';
  companyId: string;
  termsType: 'Standard' | 'Exclusive';
  folderDestination: 'working';
  vatNumber?: string;
  idNumber?: string;
  signatoryPosition?: string;
  quoteId?: number;
  orderFormNumber?: number;
  orderFormDate?: string;
  dealType?: string;
  licenseType?: string;
  agreementTerm?: string;
  confirmedBy?: string;
  approvedBy?: string;
  effectiveDate?: string;
  subscriptionInvoiceDate?: string;
  isConsolidated: boolean;
  selectedConsolidatedCompanies: string[];
  allHierarchyCompanies: HierarchyCompany[];
  onFolderCreated?: (folderIds: {
    parentFolderId?: string;
    workingFolderId?: string;
    agreementsFolderId?: string;
    proposalsFolderId?: string;
  }) => void;
}

export const TermsGenerationDialog: React.FC<TermsGenerationDialogProps> = ({
  open,
  onClose,
  onSuccess,
  company,
  companyName,
  registrationNumber,
  registrationType,
  address,
  signatory,
  dateOfIssue,
  brand,
  country,
  termsType,
  companyId,
  idNumber,
  folderDestination,
  vatNumber,
  signatoryPosition,
  quoteId,
  orderFormNumber,
  orderFormDate,
  dealType,
  licenseType,
  agreementTerm,
  confirmedBy,
  approvedBy,
  effectiveDate,
  subscriptionInvoiceDate,
  isConsolidated,
  selectedConsolidatedCompanies,
  allHierarchyCompanies,
  onFolderCreated,
}) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [downloadLink, setDownloadLink] = useState<string | null>(null);
  const [documentId, setDocumentId] = useState<string | null>(null);
  const [generatedFolderId, setGeneratedFolderId] = useState<string | null>(
    null,
  );
  const [newlyCreatedFolderUrl, setNewlyCreatedFolderUrl] = useState<
    string | null
  >(null);
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [isGeneratingDocument, setIsGeneratingDocument] = useState(false);
  const [folderCreationCompleted, setFolderCreationCompleted] = useState(false);
  const [consolidatedSuccessData, setConsolidatedSuccessData] =
    useState<CreateConsolidatedTermsResponse | null>(null);

  const [identifierTypeForNewFolder, setIdentifierTypeForNewFolder] = useState<
    'registration' | 'vat' | 'unknown'
  >('registration');
  const [identifierValueForNewFolder, setIdentifierValueForNewFolder] =
    useState('');
  const [identifierValidationError, setIdentifierValidationError] =
    useState<string>('');

  const validateIdentifier = (value: string): boolean => {
    if (!value.trim()) return true;
    const hasNumbers = /\d/.test(value);
    const hasLetters = /[a-zA-Z]/.test(value);
    return hasNumbers && hasLetters;
  };

  const blankFields = useMemo(() => {
    const fields: string[] = [];
    if (!registrationNumber && registrationType !== 'vat')
      fields.push('Registration Number');
    if (!vatNumber && registrationType !== 'registration')
      fields.push('VAT Number');
    if (!address) fields.push('Company Address');
    if (!signatory) fields.push('Signatory');
    // we dont check for siggnatory position if brand is konnekt
    // because it is not required
    if (brand !== 'Konnekt' && !signatoryPosition)
      fields.push('Signatory Position');

    if (brand === 'Talexio') {
      if (!orderFormNumber) fields.push('Order Form Number');
      if (!orderFormDate) fields.push('Order Form Date');
      if (!dealType) fields.push('Deal Type');
      if (!licenseType) fields.push('License Type');
      if (!agreementTerm) fields.push('Agreement Term');
      if (!confirmedBy) fields.push('Confirmed By');
      if (!approvedBy) fields.push('Approved By');
      if (!effectiveDate) fields.push('Effective Date');
      if (!subscriptionInvoiceDate) fields.push('Subscription Invoice Date');
    }
    return fields;
  }, [
    registrationNumber,
    registrationType,
    vatNumber,
    address,
    signatory,
    signatoryPosition,
    brand,
    orderFormNumber,
    orderFormDate,
    dealType,
    licenseType,
    agreementTerm,
    confirmedBy,
    approvedBy,
    effectiveDate,
    subscriptionInvoiceDate,
  ]);

  useEffect(() => {
    if (open) {
      setError(null);
      setSuccess(false);
      setDownloadLink(null);
      setDocumentId(null);
      setGeneratedFolderId(null);
      setNewlyCreatedFolderUrl(null);
      setIsCreatingFolder(false);
      setIsGeneratingDocument(false);
      setFolderCreationCompleted(false);

      if (registrationNumber) {
        setIdentifierTypeForNewFolder('registration');
        setIdentifierValueForNewFolder(registrationNumber);
      } else if (vatNumber) {
        setIdentifierTypeForNewFolder('vat');
        setIdentifierValueForNewFolder(vatNumber);
      } else {
        setIdentifierTypeForNewFolder('unknown');
        setIdentifierValueForNewFolder('');
      }
    }
  }, [open, registrationNumber, vatNumber]);

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);
    setGeneratedFolderId(null);
    setNewlyCreatedFolderUrl(null);
    setIsCreatingFolder(false);
    setIsGeneratingDocument(false);
    setFolderCreationCompleted(false);
    setConsolidatedSuccessData(null);

    try {
      if (isConsolidated) {
        setIsGeneratingDocument(true);

        const companiesForRequest = allHierarchyCompanies.filter((c) =>
          selectedConsolidatedCompanies.includes(c.companyId),
        );

        const request = {
          service: brand,
          country: country,
          documentSubType: brand === 'Konnekt' ? termsType : undefined,
          signatory: signatory || undefined,
          date: dateOfIssue.toISOString().split('T')[0],
          vatNumber: vatNumber || undefined,
          signatoryPosition: signatoryPosition || undefined,
          companies: companiesForRequest.map((c) => ({
            companyId: c.companyId,
            companyName: c.companyName,
            cNumber: c.CNumber || undefined,
            registeredAddress: c.registeredAddress || undefined,
            parentFolderId: c.parentFolderId || '',
            country: country,
          })),
        };

        const result = await createConsolidatedTerms(request);
        setConsolidatedSuccessData(result);
        setSuccess(true);
        if (onSuccess) {
          onSuccess();
        }
      } else {
        let targetFolderId: string | undefined = undefined;
        let companyFolderUrlForLink: string | undefined = undefined;

        if (!company?.parent_folder_id) {
          let regNumForFolderCreation = '';
          if (
            identifierTypeForNewFolder === 'registration' ||
            identifierTypeForNewFolder === 'vat'
          ) {
            if (!identifierValueForNewFolder.trim()) {
              setError(
                `The ${
                  identifierTypeForNewFolder === 'registration'
                    ? 'Registration Number'
                    : 'VAT Number'
                } cannot be empty when selected for folder creation.`,
              );
              setLoading(false);
              return;
            }
            if (!validateIdentifier(identifierValueForNewFolder.trim())) {
              setError(
                `The ${
                  identifierTypeForNewFolder === 'registration'
                    ? 'Registration Number'
                    : 'VAT Number'
                } must contain both letters and numbers.`,
              );
              setLoading(false);
              return;
            }
            regNumForFolderCreation = identifierValueForNewFolder.trim();
          }

          setIsCreatingFolder(true);
          const newFolderDataResponse = await createDriveFolder(
            companyName,
            regNumForFolderCreation,
            companyId,
          );
          setIsCreatingFolder(false);
          setFolderCreationCompleted(true);

          const createdFolders = newFolderDataResponse.data.created_folders;
          companyFolderUrlForLink =
            newFolderDataResponse.data.company_folder_url;

          const workingDocsFolder = createdFolders.find(
            (f) => f.name === 'Old Agreements & Working Documents',
          );
          const agreementsFolder = createdFolders.find(
            (f) => f.name === 'Agreements',
          );
          const proposalsFolder = createdFolders.find(
            (f) => f.name === 'Proposals',
          );
          const parentFolder = createdFolders.find((f) => f.type === 'parent');

          targetFolderId = workingDocsFolder?.id || createdFolders[0]?.id;
          setNewlyCreatedFolderUrl(companyFolderUrlForLink);

          if (onFolderCreated) {
            onFolderCreated({
              parentFolderId: parentFolder?.id,
              workingFolderId: workingDocsFolder?.id,
              agreementsFolderId: agreementsFolder?.id,
              proposalsFolderId: proposalsFolder?.id,
            });
          }
        } else if (company?.parent_folder_id) {
          targetFolderId =
            company.working_documents_folder_id ||
            company.agreements_folder_id ||
            company.parent_folder_id;
          setGeneratedFolderId(targetFolderId);
        }

        const formattedDate = dateOfIssue.toISOString().split('T')[0];

        const request = {
          service: brand,
          country: country,
          companyName: companyName,
          documentSubType: brand === 'Konnekt' ? termsType : undefined,
          cNumber: registrationNumber || undefined,
          vatNumber: vatNumber || undefined,
          registeredAddress: address || undefined,
          signatory: signatory || undefined,
          date: formattedDate,
          folderId: targetFolderId,
          companyId: companyId,
          signatoryPosition: signatoryPosition || undefined,
          quoteId: brand === 'Talexio' ? quoteId : undefined,
          effectiveDate:
            (brand === 'Talexio' || brand === 'SIM') && effectiveDate
              ? effectiveDate
              : undefined,
          ...(brand === 'Talexio' && {
            orderFormNumber,
            orderFormDate,
            dealType,
            licenseType,
            agreementTerm,
            confirmedBy,
            approvedBy,
            subscriptionInvoiceDate,
          }),
        };

        setIsGeneratingDocument(true);
        const result = await createTerms(request);

        if (result.download_link) {
          setDownloadLink(result.download_link);
        }
        setDocumentId(result.document_id);
        setSuccess(true);

        if (onSuccess) {
          onSuccess();
        }
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to generate agreement',
      );
    } finally {
      setLoading(false);
      setIsCreatingFolder(false);
      setIsGeneratingDocument(false);
    }
  };

  const handleIdentifierTypeChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newType = event.target.value as 'registration' | 'vat' | 'unknown';
    setIdentifierTypeForNewFolder(newType);
    setIdentifierValidationError('');
    if (newType === 'registration') {
      setIdentifierValueForNewFolder(registrationNumber || '');
    } else if (newType === 'vat') {
      setIdentifierValueForNewFolder(vatNumber || '');
    } else {
      setIdentifierValueForNewFolder('');
    }
  };

  const handleIdentifierValueChange = (value: string) => {
    setIdentifierValueForNewFolder(value);
    if (value.trim() && !validateIdentifier(value.trim())) {
      setIdentifierValidationError(
        `${
          identifierTypeForNewFolder === 'registration'
            ? 'Registration Number'
            : 'VAT Number'
        } must contain both letters and numbers.`,
      );
    } else {
      setIdentifierValidationError('');
    }
  };

  if (loading) {
    const showDocStep = !company?.parent_folder_id
      ? folderCreationCompleted
      : true;

    return (
      <Dialog
        open={open}
        onClose={onClose}
        disableBackdropClick
        disableEscapeKeyDown
      >
        <DialogTitle>Processing Request</DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Box sx={{ width: '100%', my: 2 }}>
            {!company?.parent_folder_id && (
              <Box
                display="flex"
                alignItems="center"
                mb={showDocStep ? 1 : 0}
                py={1}
              >
                {isCreatingFolder ? (
                  <CircularProgress size={20} style={{ marginRight: '8px' }} />
                ) : folderCreationCompleted ? (
                  <CheckCircleOutlineIcon
                    style={{ color: 'green', marginRight: '8px' }}
                  />
                ) : (
                  <HourglassEmptyIcon style={{ marginRight: '8px' }} />
                )}
                <Typography variant="body1">
                  {isCreatingFolder
                    ? 'Creating Google Drive folder...'
                    : folderCreationCompleted
                    ? 'Google Drive folder created!'
                    : 'Google Drive folder: Pending'}
                </Typography>
              </Box>
            )}

            {showDocStep && (
              <Box display="flex" alignItems="center" py={1}>
                {isGeneratingDocument ? (
                  <CircularProgress size={20} style={{ marginRight: '8px' }} />
                ) : (
                  <HourglassEmptyIcon style={{ marginRight: '8px' }} />
                )}
                <Typography variant="body1">
                  {isGeneratingDocument
                    ? 'Generating agreement document...'
                    : 'Agreement document: Pending'}
                </Typography>
              </Box>
            )}
          </Box>
          <DialogContentText
            align="center"
            style={{ marginTop: '16px', fontSize: '0.9em', color: '#666' }}
          >
            This process can take some time. Please wait.
          </DialogContentText>
        </DialogContent>
      </Dialog>
    );
  }

  if (success) {
    return (
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>Agreement Generated Successfully</DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <div className={classes.successContainer}>
            {isConsolidated && consolidatedSuccessData ? (
              <>
                <Alert
                  severity="success"
                  style={{ width: '100%', justifyContent: 'center' }}
                >
                  {consolidatedSuccessData.message}
                </Alert>
                <Typography
                  variant="body2"
                  style={{
                    textAlign: 'left',
                    width: '100%',
                  }}
                >
                  The following agreements have been generated and saved to
                  their respective Google Drive folders:
                </Typography>
                <Paper
                  variant="outlined"
                  style={{
                    width: '100%',
                    maxHeight: 200,
                    overflow: 'auto',
                  }}
                >
                  <List dense>
                    {consolidatedSuccessData.created_terms.map((term) => (
                      <ListItem key={term.document_id}>
                        <ListItemIcon>
                          <DescriptionIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary={term.company_name}
                          secondary={`Terms ID: ${term.terms_id}`}
                        />
                        <ListItemSecondaryAction>
                          <Button
                            variant="outlined"
                            size="small"
                            href={term.view_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            startIcon={<LaunchIcon />}
                          >
                            Open
                          </Button>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </>
            ) : (
              <>
                <Alert severity="success">
                  {!company?.parent_folder_id && newlyCreatedFolderUrl
                    ? 'Folder created and agreement generated successfully!'
                    : 'Agreement generated successfully!'}
                </Alert>

                {downloadLink && (
                  <Link
                    href={downloadLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.successLink}
                  >
                    Click here to download your agreement. Please be sure to
                    upload it to the correct Drive folder for visibility.
                  </Link>
                )}

                {!downloadLink && documentId && (
                  <>
                    <Typography variant="body1" className={classes.successLink}>
                      {!company?.parent_folder_id && newlyCreatedFolderUrl
                        ? 'Your agreement has been saved to the newly created Drive folder.'
                        : 'Your agreement has been saved to the selected Drive folder.'}
                    </Typography>
                    <Box className={classes.successActions}>
                      <Button
                        variant="outlined"
                        href={`https://docs.google.com/document/d/${documentId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Open File
                      </Button>
                      {(generatedFolderId || newlyCreatedFolderUrl) && (
                        <Button
                          variant="outlined"
                          href={
                            newlyCreatedFolderUrl ||
                            `https://drive.google.com/drive/folders/${generatedFolderId}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Open Folder
                        </Button>
                      )}
                    </Box>
                  </>
                )}
              </>
            )}
            <Typography
              variant="caption"
              color="textSecondary"
              style={{ marginTop: '16px' }}
            >
              Remember to review the generated agreement for accuracy before
              sharing it with the client.
            </Typography>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        {isConsolidated
          ? 'Generate Consolidated Agreement'
          : 'Generate Agreement'}
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        {isConsolidated ? (
          <>
            <DialogContentText>
              You are about to generate consolidated terms of business for the
              following companies:
            </DialogContentText>
            <Box className={classes.consolidatedInfo}>
              <ul className={classes.companyList}>
                {allHierarchyCompanies
                  .filter((c) =>
                    selectedConsolidatedCompanies.includes(c.companyId),
                  )
                  .map((c) => (
                    <li key={c.companyId}>
                      <Typography variant="body2">
                        {c.companyName} {c.CNumber ? `(${c.CNumber})` : ''}
                      </Typography>
                    </li>
                  ))}
              </ul>
            </Box>

            {allHierarchyCompanies
              .filter((c) =>
                selectedConsolidatedCompanies.includes(c.companyId),
              )
              .some((c) => !c.parentFolderId) && (
              <Alert
                severity="warning"
                className={classes.missingFolderWarning}
              >
                <Typography variant="body2" gutterBottom>
                  <strong>
                    The following companies do not have Drive folders:
                  </strong>
                </Typography>
                <ul style={{ marginTop: 8, marginBottom: 0 }}>
                  {allHierarchyCompanies
                    .filter(
                      (c) =>
                        selectedConsolidatedCompanies.includes(c.companyId) &&
                        !c.parentFolderId,
                    )
                    .map((c) => (
                      <li key={c.companyId}>
                        <Typography variant="body2">{c.companyName}</Typography>
                      </li>
                    ))}
                </ul>
                <Typography variant="body2" style={{ marginTop: 8 }}>
                  Drive folders will be automatically created for these
                  companies, and the consolidated agreement will be copied to
                  each folder.
                </Typography>
              </Alert>
            )}

            <DialogContentText style={{ marginTop: 16 }}>
              A master agreement will be generated and copied to each company's
              Drive folder.
            </DialogContentText>
          </>
        ) : (
          <DialogContentText>
            How do you want to save the agreement for {companyName}?
          </DialogContentText>
        )}

        {error && (
          <Alert
            severity="error"
            style={{ marginTop: '16px', marginBottom: '16px' }}
          >
            {error}
          </Alert>
        )}

        {blankFields.length > 0 && !isConsolidated && (
          <Alert
            severity="warning"
            style={{ marginTop: '16px', marginBottom: '16px' }}
          >
            <Typography variant="body2">
              The following optional fields are blank:
            </Typography>
            <ul className={classes.warningList}>
              {blankFields.map((field) => (
                <li key={field}>
                  <Typography variant="caption">{field}</Typography>
                </li>
              ))}
            </ul>
            <Typography variant="body2" style={{ marginTop: '8px' }}>
              They will be replaced with "N/A". Please ensure these are manually
              filled in the generated document if required.
            </Typography>
          </Alert>
        )}

        {!isConsolidated && (
          <>
            {company?.parent_folder_id ? (
              <Alert severity="info" className={classes.infoBox}>
                An existing Google Drive folder was found. The generated
                agreement will be saved there automatically.
              </Alert>
            ) : (
              <>
                <Alert severity="info" className={classes.infoBox}>
                  A Google Drive folder for this company does not exist. A new
                  one will be created automatically.
                </Alert>
                <Box mt={2} mb={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    Folder Identifier:
                  </Typography>
                  <FormControl
                    component="fieldset"
                    className={classes.formControl}
                    style={{ width: 'auto' }}
                  >
                    <RadioGroup
                      aria-label="identifier-type-for-new-folder"
                      name="identifier-type-for-new-folder"
                      value={identifierTypeForNewFolder}
                      onChange={handleIdentifierTypeChange}
                      row
                    >
                      <FormControlLabel
                        value="registration"
                        control={<Radio />}
                        label="Registration No."
                      />
                      <FormControlLabel
                        value="vat"
                        control={<Radio />}
                        label="VAT No."
                      />
                      <FormControlLabel
                        value="unknown"
                        control={<Radio />}
                        label="Unknown"
                      />
                    </RadioGroup>
                  </FormControl>
                  {(identifierTypeForNewFolder === 'registration' ||
                    identifierTypeForNewFolder === 'vat') && (
                    <TextField
                      variant="outlined"
                      label={
                        identifierTypeForNewFolder === 'registration'
                          ? 'Registration Number'
                          : 'VAT Number'
                      }
                      value={identifierValueForNewFolder}
                      onChange={(e) =>
                        handleIdentifierValueChange(e.target.value)
                      }
                      fullWidth
                      margin="dense"
                      InputLabelProps={{ shrink: true }}
                      error={!!identifierValidationError}
                      helperText={identifierValidationError || undefined}
                    />
                  )}
                  <Typography variant="body2" style={{ marginTop: '8px' }}>
                    The new folder will be named:{' '}
                    <strong
                      dangerouslySetInnerHTML={{
                        __html:
                          identifierTypeForNewFolder !== 'unknown' &&
                          identifierValueForNewFolder.trim()
                            ? `${identifierValueForNewFolder.trim()} - ${companyName}`
                            : companyName,
                      }}
                    />
                  </Typography>
                  {identifierTypeForNewFolder === 'unknown' && (
                    <Alert severity="warning" style={{ marginTop: '16px' }}>
                      <strong>
                        It is strongly recommended to add a unique identifier to
                        avoid confusion with other companies.
                      </strong>
                    </Alert>
                  )}
                </Box>
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleGenerate}
          color="primary"
          variant="contained"
          disabled={
            loading ||
            (!isConsolidated &&
              !company?.parent_folder_id &&
              identifierTypeForNewFolder !== 'unknown' &&
              (identifierTypeForNewFolder === 'registration' ||
                identifierTypeForNewFolder === 'vat') &&
              (!identifierValueForNewFolder.trim() ||
                !!identifierValidationError))
          }
        >
          {isConsolidated
            ? 'Generate Consolidated Agreement'
            : 'Generate Agreement'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TermsGenerationDialog;
